import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import Pagination from '../components/Pagination';
import { usePageSize } from '../hooks/usePagination';
import './Customers.css';
import { AuthContext } from '../context/AuthContext';

// "23 Sep 2026" / "11:02 am" for the Joined column
const formatJoinedAt = (value) => {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
  };
};

const Customers = () => {
  const { hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [total, setTotal] = useState(0);
  const [togglingStatus, setTogglingStatus] = useState({});

  useEffect(() => {
    fetchCustomers();
  }, [page, pageSize, filterActive, searchTerm]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(filterActive && { isActive: filterActive })
      };
      const response = await axios.get(`${API_BASE_URL}/admin/customers`, { params });
      setCustomers(response.data.data || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch customers');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterActive(e.target.value);
    setPage(1);
  };

  const handleToggleActive = async (customerId, currentStatus) => {
    const newStatus = !currentStatus;
    setTogglingStatus(prev => ({ ...prev, [customerId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/customers/${customerId}`, {
        isActive: newStatus
      });
      
      // Update the customer in the local state
      setCustomers(customers.map(customer => 
        customer._id === customerId 
          ? { ...customer, isActive: response.data.data.isActive }
          : customer
      ));
    } catch (error) {
      setError('Failed to update customer status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [customerId]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/customers/${id}`);
      fetchCustomers();
    } catch (error) {
      setError('Failed to delete customer');
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString();
  };

  const getLoginType = (customer) => {
    if (customer.socialLogin?.provider) {
      const provider = customer.socialLogin.provider;
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
    if (customer.phone) {
      return 'Phone';
    }
    if (customer.email) {
      return 'Email';
    }
    return '—';
  };

  const getLoginTypeDataAttr = (customer) => {
    if (customer.socialLogin?.provider) {
      return customer.socialLogin.provider.toLowerCase();
    }
    if (customer.phone) {
      return 'phone';
    }
    if (customer.email) {
      return 'email';
    }
    return '';
  };

  if (loading && customers.length === 0) {
    return (
      <Layout>
        <div className="container">
          <div className="customers-header">
            <h1>Customers</h1>
            <div className="header-stats">
              <div className="skeleton skeleton-text" style={{ width: '100px', height: '36px', borderRadius: '8px' }}></div>
            </div>
          </div>
          <div className="customers-filters">
            <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '10px' }}></div>
            <div className="skeleton skeleton-text" style={{ width: '150px', height: '44px', borderRadius: '10px' }}></div>
          </div>
          <div className="customers-table-container">
            <table className="customers-table">
              <thead>
                <tr>
                  <th>Joined</th>
                  <th>Profile</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Login Type</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '90px', height: '14px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '60px', height: '12px', marginTop: '6px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-image" style={{ width: '40px', height: '40px', borderRadius: '50%' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80%', height: '14px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="customers-header">
          <h1>Customers</h1>
          <div className="header-stats">
            <span className="stat-badge">Total: {total}</span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="customers-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="filter-box">
            <select value={filterActive} onChange={handleFilterChange} className="filter-select">
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {customers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h2>No customers found</h2>
            <p>No customers match your search criteria.</p>
          </div>
        ) : (
          <>
            <div className="customers-table-container">
              <table className="customers-table">
                <thead>
                  <tr>
                    <th>Joined</th>
                    <th>Profile</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Login Type</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => {
                    const isTogglingStatus = togglingStatus[customer._id];
                    return (
                      <tr key={customer._id}>
                        <td>
                          <div className="hotel-created-cell">
                            <span className="created-date">{formatJoinedAt(customer.createdAt).date}</span>
                            <span className="created-time">{formatJoinedAt(customer.createdAt).time}</span>
                          </div>
                        </td>
                        <td>
                          <div className="customer-profile-cell">
                            {customer.profilePicture ? (
                              <img
                                src={`${API_BASE_URL_WITHOUT_API}${customer.profilePicture}`}
                                alt={customer.displayName || customer.name || 'Customer'}
                                className="customer-profile-image"
                              />
                            ) : (
                              <div className="customer-profile-placeholder">
                                {(customer.displayName || customer.name || 'C').charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="customer-name-cell">
                            <span className="customer-name-text">{customer.displayName || customer.name || '—'}</span>
                            {customer.displayName && customer.name && customer.displayName !== customer.name && (
                              <span className="customer-secondary-name">{customer.name}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="customer-contact-cell">
                            {customer.phone && (
                              <div className="contact-item">
                                <span className="contact-icon">📱</span>
                                <span>{customer.phone}</span>
                              </div>
                            )}
                            {customer.email && (
                              <div className="contact-item">
                                <span className="contact-icon">✉️</span>
                                <span className="contact-email">{customer.email}</span>
                              </div>
                            )}
                            {!customer.phone && !customer.email && <span className="contact-empty">—</span>}
                          </div>
                        </td>
                        <td>
                          <div className="customer-login-type-cell">
                            <span 
                              className="login-type-badge" 
                              data-login-type={getLoginTypeDataAttr(customer)}
                            >
                              {getLoginType(customer)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="status-cell">
                            <label className="status-toggle-label">
                              <div className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={customer.isActive}
                                  onChange={() => handleToggleActive(customer._id, customer.isActive)}
                                  disabled={isTogglingStatus}
                                  className="toggle-input"
                                />
                                <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                              </div>
                              <span className={`status-text ${customer.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                                {customer.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td>
                          <div className="customer-lastlogin-cell">
                            <span>{formatDate(customer.lastLogin)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="table-actions">
                            {hasPermission('customers.update') && (
                              <button
                                onClick={() => navigate(`/sy-admin/customers/edit/${customer._id}`)}
                                className="btn btn-edit btn-icon-only"
                                title="Edit Customer"
                              >
                                <span className="btn-icon">✏️</span>
                              </button>
                            )}
                            {hasPermission('customers.delete') && (
                              <button
                                onClick={() => handleDelete(customer._id)}
                                className="btn btn-delete btn-icon-only"
                                title="Delete Customer"
                              >
                                <span className="btn-icon">🗑️</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pages={totalPages}
              total={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              loading={loading}
              noun="customers"
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default Customers;

